(ns common.schema.entry-criteria.model
  "Malli schemas for the stage-entry-criteria feature.

   Documentation-first: these schemas define the vocabulary used by the
   validator (`common.schema.entry-criteria.utils`) and the cljs wrapper
   (`ui.view.entry-criteria.utils`). Opt-in validation can be added at
   boundaries later via `malli.core/validate`.

   See docs/functional/stage_entry_criteria/ for the design.")

(def Scope
  "Which of the three entities a field criterion or association criterion is
   anchored on. Determined for fields by which per-scope fields-stage-config
   the criterion was emitted from, and for associations by the source-entity
   side of the `:association-key`."
  [:enum :opportunity :target :target-profile])

(def CriterionKind
  "What kind of entry criterion is unmet."
  [:enum :field-blank :field-invalid :association-missing])

(def Setting
  "Per-stage setting on a resolved field or association config item. Matches
   `common.schema.entity-creator.utils/visible-settings`."
  [:enum :mandatory :optional :system-mandatory])

(def StageId
  "Datomic id of an `:entity.type/stage` (or one of the per-opportunity-type
   stage entity-types — see `common.schema.entities.pipeline/entity-type-mappings`)."
  int?)

(def AssociationKey
  "Tuple identifying a person-association configured on the creator-config:
   `[source-entity-type other-entity-type association-type-keyword]`. The
   third element is the value of `:entity-association/type` on actual
   `:entity.type/entity-association` entities."
  [:tuple keyword? keyword? keyword?])

(def StageSetting
  "Resolved per-stage configuration block for a field or association. The
   non-`:setting` keys are added by `resolve-stage-config` /
   `resolve-association-stage-config` to record provenance."
  [:map
   [:setting Setting]
   [:system-mandatory? {:optional true} boolean?]
   [:inherited? {:optional true} boolean?]
   [:default? {:optional true} boolean?]
   [:non-editable? {:optional true} boolean?]])

(def FieldStageConfig
  "Single field item inside a resolved fields-stage-config (output of
   `common.schema.entity-creator.utils/resolve-fields-stage-config`)."
  [:map
   [:attribute keyword?]
   [:stages [:map-of StageId StageSetting]]])

(def FieldsStageConfig
  "Resolved fields-stage-config for one scope (opportunity / target /
   target-profile). Other keys may exist on the underlying config (e.g.
   target-profile-specific entries) — only `:fields` is read by the validator."
  [:map
   [:fields [:sequential FieldStageConfig]]])

(def AssociationStageConfig
  "Single association item inside a resolved associations-stage-config.
   `:scope` is injected by `ui.view.entry-criteria.utils/with-association-scopes`
   from the `:association-key`'s source-entity-type; the cljc validator does
   not derive it itself."
  [:map
   [:association-key AssociationKey]
   [:stages [:map-of StageId StageSetting]]
   [:scope {:optional true} [:maybe Scope]]])

(def AssociationsStageConfig
  "Resolved associations-stage-config (output of
   `common.schema.entity-creator.utils/resolve-associations-stage-config`,
   optionally enriched with `:scope` on each item by the cljs wrapper)."
  [:map
   [:associations [:sequential AssociationStageConfig]]])

(def EntityAssociation
  "Pulled `:entity.type/entity-association` entity. Only the keys the
   validator reads are listed; real entities carry more (source-entity,
   target-entity refs, etc.)."
  [:map
   [:entity-association/type keyword?]])

(def AssociationsByScope
  "Scope-keyed bundle of pulled `:entity.type/entity-association` entities,
   supplied to the validator instead of letting it reach into Datomic."
  [:map-of Scope [:sequential EntityAssociation]])

(def EntityTypes
  "Entity-types of the three scope entities, used by the cljs wrapper to
   derive `:scope` for each association config item."
  [:map
   [:opportunity-entity-type keyword?]
   [:target-entity-type {:optional true} [:maybe keyword?]]
   [:target-profile-entity-type {:optional true} [:maybe keyword?]]])

(def Criterion
  "One unmet entry criterion in the validator's output. Either a field
   criterion (`:field-blank` / `:field-invalid` — carries `:attribute`) or an
   association criterion (`:association-missing` — carries `:association-key`
   and `:association-type`). `:reason` is set only for `:field-invalid` and
   carries whatever shape the injected `validate-field-fn` returns."
  [:map {:closed true}
   [:scope Scope]
   [:kind CriterionKind]
   [:write-disabled? boolean?]
   [:attribute {:optional true} keyword?]
   [:association-key {:optional true} AssociationKey]
   [:association-type {:optional true} keyword?]
   [:reason {:optional true} any?]])

(def Criteria
  "Output of `resolve-stage-entry-criteria`."
  [:sequential Criterion])

(def entry-criteria-modes
  "The one list of the values of `:entry-criteria-mode`, in the order that the
   settings modal shows them. The key is stored in the main config of a
   pipeline's creator-config. The settings modal builds its options from this
   list, `EntryCriteriaMode` is derived from it, and the backend refuses any
   other value when the creator-config is saved. Add a mode here, give it a
   label in `entry-criteria-mode-label-keys`, and decide in `review-modes`
   whether it can open a review.

   `:missing-mandatory` opens the modal only for an unmet criterion. A config
   that sets no mode has it. `:missing-any` also opens the modal for a blank
   optional item. `:always` opens it on every stage move."
  [:missing-mandatory :missing-any :always])

(def default-entry-criteria-mode
  "The mode of a config that sets none, and so the behavior that existed before
   the modes did."
  :missing-mandatory)

(def review-modes
  "The modes in which the modal can open although nothing blocks the move, to
   show all items that the config sets for the stage. See
   `common.schema.entry-criteria.utils/review-for-stage`."
  [:missing-any :always])

(def entry-criteria-mode-label-keys
  "The localization key of the label of each mode. Every place that lists the
   modes takes the label from here."
  {:missing-mandatory :creator-config/entry-criteria-mode-missing-mandatory
   :missing-any :creator-config/entry-criteria-mode-missing-any
   :always :creator-config/entry-criteria-mode-always})

(def EntryCriteriaMode
  "When the entry-criteria modal opens on a stage move. Stored as
   `:entry-criteria-mode` in the main config of a pipeline's creator-config; an
   absent value means `default-entry-criteria-mode`."
  (into [:enum] entry-criteria-modes))

(def ReviewAssociation
  "One optional association row that a review adds to the modal. A missing
   mandatory association is a `Criterion` of kind `:association-missing`
   instead."
  [:map {:closed true}
   [:scope Scope]
   [:association-key AssociationKey]
   [:association-type {:optional true} [:maybe keyword?]]])

(def Review
  "Output of `common.schema.entry-criteria.utils/review-for-stage`. Present only
   in the modes `:missing-any` and `:always`. `:attributes` lists, for each
   scope, every field that the config shows at the target stage and that the
   user can write. The modal shows all of them, not only the failing ones."
  [:map {:closed true}
   [:mode (into [:enum] review-modes)]
   [:attributes [:map-of Scope [:vector keyword?]]]
   [:associations [:vector ReviewAssociation]]])
